import React from 'react';
import { Alert, Box, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IMPORT_MODE_REPLACE } from 'Constants/CertificateNames';
import { formatCertificateCategory } from './CertificateHelpers';

// How many problem cells to itemise. The backend counts every one and sends up
// to 100; the counts above the table are always exact.
const ISSUES_SHOWN = 20;

const Stat = ({ label, value, tone }) => (
    <Grid item xs={6} sm={4} md={2}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
            <Typography variant="h6" color={tone ?? 'text.primary'}>
                {Number(value ?? 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Paper>
    </Grid>
);

/**
 * The summary the backend returns for every import — a preview (dry run) or
 * the real thing. Every number is the server's; nothing is recounted here.
 *
 * `summary` is CertificateNameImporter::summary() as JSON.
 */
const ImportSummary = ({ summary }) => {
    const { t } = useTranslation();

    if (!summary) {
        return null;
    }

    const isReplace = summary.mode === IMPORT_MODE_REPLACE;
    const k = (key) => t(`admin.certificateNames.import.summary.${key}`);
    const issues = summary.issues ?? [];
    const hiddenIssues = Math.max(0, (summary.errors ?? 0) - Math.min(issues.length, ISSUES_SHOWN));

    return (
        <Box className="space-y-3">
            <Alert severity={summary.dry_run ? 'info' : 'success'}>
                {summary.dry_run ? k('previewNote') : k('doneNote')}
            </Alert>

            <Grid container spacing={1.5}>
                <Stat label={k('total')} value={summary.total} />
                <Stat
                    label={summary.dry_run ? k('willImport') : k('imported')}
                    value={summary.imported}
                    tone="success.main"
                />
                <Stat label={k('duplicates')} value={summary.duplicates} tone="warning.main" />
                <Stat label={k('skipped')} value={summary.skipped} />
                <Stat label={k('errors')} value={summary.errors} tone={summary.errors ? 'error.main' : undefined} />
                <Stat label={k('finalCount')} value={summary.final_count} tone="primary.main" />
                {isReplace && (
                    <>
                        <Stat label={k('kept')} value={summary.kept} />
                        <Stat
                            label={summary.dry_run ? k('willRemove') : k('removed')}
                            value={summary.removed}
                            tone={summary.removed ? 'error.main' : undefined}
                        />
                    </>
                )}
            </Grid>

            <Typography variant="body2" color="text.secondary">
                {t('admin.certificateNames.import.summary.breakdown', {
                    inFile: (summary.duplicates_in_file ?? 0).toLocaleString(),
                    inList: (summary.already_in_list ?? 0).toLocaleString(),
                    empty: (summary.empty ?? 0).toLocaleString(),
                })}
            </Typography>

            {summary.columns?.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" className="pb-1">
                        {k('columnsTitle')}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{k('column')}</TableCell>
                                <TableCell>{k('heading')}</TableCell>
                                <TableCell>{k('category')}</TableCell>
                                <TableCell align="right">{k('names')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {summary.columns.map((column) => (
                                <TableRow key={`${column.sheet}-${column.column}`}>
                                    <TableCell>{column.column}</TableCell>
                                    <TableCell>{column.heading ?? k('noHeading')}</TableCell>
                                    <TableCell>{formatCertificateCategory(t, column.category)}</TableCell>
                                    <TableCell align="right">{Number(column.count).toLocaleString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {issues.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" className="pb-1">
                        {k('issuesTitle')}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{k('row')}</TableCell>
                                <TableCell>{k('column')}</TableCell>
                                <TableCell>{k('value')}</TableCell>
                                <TableCell>{k('reason')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {issues.slice(0, ISSUES_SHOWN).map((issue, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={index}>
                                    <TableCell>{issue.row}</TableCell>
                                    <TableCell>{issue.column ?? '---'}</TableCell>
                                    <TableCell>{issue.value}</TableCell>
                                    <TableCell>
                                        {t(`admin.certificateNames.import.reasons.${issue.reason}`, issue.reason)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {hiddenIssues > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {t('admin.certificateNames.import.summary.moreIssues', { count: hiddenIssues })}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ImportSummary;
